import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, CreditCard, Lock } from "lucide-react";
import { Button } from "./ui/button";
import { SquarePaymentForm } from "./SquarePaymentForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Modale "Payer maintenant" pour les commandes ou rendez-vous au statut pending_payment.
 *
 * Props:
 *  - kind ('appointment' | 'order' | 'event')
 *  - id (string)
 *  - amount (number) — montant à régler en €
 *  - title (string) — affichage modale
 *  - buyer (object) — { name, email }
 *  - onClose (function)
 *  - onPaid (function) — appelé après succès
 */
export default function PayPendingModal({ kind, id, amount, title, buyer = {}, onClose, onPaid }) {
  const [processing, setProcessing] = useState(false);

  const endpoint = {
    appointment: `${API}/appointments/${id}/pay`,
    order: `${API}/payments/create`,
    event: `${API}/events/${id}/pay`,
  }[kind];

  const handlePay = async (sourceId, verificationToken) => {
    setProcessing(true);
    try {
      const body = kind === "order"
        ? { order_id: id, source_id: sourceId, verification_token: verificationToken }
        : { source_id: sourceId, verification_token: verificationToken };
      await axios.post(endpoint, body, { withCredentials: true });
      toast.success("Paiement validé ✅");
      if (onPaid) onPaid();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec du paiement");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="pay-pending-modal"
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          className="relative w-full max-w-md bg-[#261B3B] border border-white/15 rounded-2xl p-6 shadow-2xl"
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition-colors"
            data-testid="pay-pending-close"
          >
            <X className="w-4 h-4 text-[#B59ED1]" />
          </button>

          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-5 h-5 text-[#D926A9]" />
            <h2 className="font-serif text-xl text-[#F7F3FC]">Finaliser le paiement</h2>
          </div>
          <p className="text-sm text-[#B59ED1] mb-4">{title}</p>

          <div className="mb-4 rounded-xl border border-[#00E5FF]/25 bg-[#00E5FF]/5 p-3">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-3.5 h-3.5 text-[#00E5FF]" />
              <span className="text-xs font-medium text-[#F7F3FC]">Paiement 100% sécurisé · Square</span>
            </div>
            <p className="text-[10px] text-[#B59ED1]">Tes coordonnées bancaires sont chiffrées et jamais stockées sur nos serveurs.</p>
          </div>

          <SquarePaymentForm
            amount={amount}
            buyerEmail={buyer.email || ""}
            buyerName={buyer.name || ""}
            onSuccess={handlePay}
            disabled={processing}
            buttonText="Payer"
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
